"use client";

import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Search,
  ChevronRight,
  UserCog,
  Edit,
  X,
  Check,
  RefreshCw,
  CheckCircle,
  AlertCircle,
  Trash,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "react-toastify";

export default function UsersTab() {
  const navigate = useNavigate();
  const { user, token, loading } = useAuth();
  const [searchTerm, setSearchTerm] = useState("");
  const [users, setUsers] = useState([]);
  const [availablePlans, setAvailablePlans] = useState([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [isLoadingUsers, setIsLoadingUsers] = useState(false);
  const [isLoadingPlans, setIsLoadingPlans] = useState(false);
  const [userReports, setUserReports] = useState({});

  const capitalizeFirstLetter = (str) => {
    if (!str || typeof str !== "string") return "";
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  };

  useEffect(() => {
    if (loading) return;
    if (!user || !token) {
      toast.error("Please log in to access this page.", {
        position: "top-right",
        icon: <X size={20} className="text-red-600" />,
      });
      navigate("/login");
      return;
    }
    if (user.role !== "Admin" && user.role !== "Super Admin") {
      toast.error("You are not authorized to access this page.", {
        position: "top-right",
        icon: <X size={20} className="text-red-600" />,
      });
      navigate("/whyus");
    }
  }, [user, token, loading, navigate]);

  const fetchUsers = async () => {
    setIsLoadingUsers(true);
    try {
      const response = await axios.get("https://backend.emerald.azadnoori.com/api/v1/users", {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true,
      });
      const fetchedUsers = response.data.data.users || [];
      const sortedUsers = fetchedUsers.sort((a, b) => {
        if (a.role === "Super Admin" && b.role !== "Super Admin") return -1;
        if (a.role !== "Super Admin" && b.role === "Super Admin") return 1;
        return 0;
      });
      setUsers(sortedUsers);
      localStorage.setItem("users", JSON.stringify(sortedUsers));
      localStorage.setItem("usersCacheTime", JSON.stringify(Date.now()));
      const reportsData = {};
      for (const user of sortedUsers) {
        const reports = await fetchUserReports(user.id);
        reportsData[user.id] = reports;
      }
      setUserReports(reportsData);
    } catch (error) {
      if (error.name === "AbortError") return;
      if (error.response?.status === 401) {
        toast.error("Session expired. Please log in again.", {
          position: "top-right",
          icon: <X size={20} className="text-red-600" />,
        });
        navigate("/login");
      } else if (error.response?.status === 403) {
        toast.error("Not authorized to fetch users.", {
          position: "top-right",
          icon: <X size={20} className="text-red-600" />,
        });
      } else if (error.response?.status === 429) {
        const retryAfter = error.response?.headers["retry-after"];
        toast.error(
          `Too many requests. Please try again later.${
            retryAfter ? ` Retry after ${retryAfter} seconds.` : ""
          }`,
          {
            position: "top-right",
            icon: <X size={20} className="text-red-600" />,
          }
        );
      } else {
        toast.error(error.response?.data?.message || "Failed to fetch users.", {
          position: "top-right",
          icon: <X size={20} className="text-red-600" />,
        });
      }
    } finally {
      setIsLoadingUsers(false);
    }
  };

  const fetchUserReports = async (userId) => {
    try {
      const response = await axios.get(
        `https://backend.emerald.azadnoori.com/api/v1/reports/userreports/${userId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true,
        }
      );
      const reports = response.data.data.reports.map((report) => ({
        id: report.id,
        images: report.images || [],
      }));
      return {
        count: reports.length,
        imageCount: reports.reduce((sum, report) => sum + report.images.length, 0),
      };
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error(`Not authorized to fetch reports for user ${userId}.`, {
          position: "top-right",
          icon: <X size={20} className="text-red-600" />,
        });
      }
      return { count: 0, imageCount: 0 };
    }
  };

  useEffect(() => {
    if (!user || !token || (user.role !== "Admin" && user.role !== "Super Admin")) return;

    let mounted = true;
    const controller = new AbortController();

    const cachedUsers = JSON.parse(localStorage.getItem("users"));
    const cacheTime = JSON.parse(localStorage.getItem("usersCacheTime"));
    const isCacheValid = cacheTime && Date.now() - cacheTime < 5 * 60 * 1000;

    if (cachedUsers && isCacheValid) {
      const sortedUsers = cachedUsers.sort((a, b) => {
        if (a.role === "Super Admin" && b.role !== "Super Admin") return -1;
        if (a.role !== "Super Admin" && b.role === "Super Admin") return 1;
        return 0;
      });
      setUsers(sortedUsers);
      const fetchReportsForCached = async () => {
        const reportsData = {};
        for (const user of sortedUsers) {
          const reports = await fetchUserReports(user.id);
          reportsData[user.id] = reports;
        }
        if (mounted) setUserReports(reportsData);
      };
      fetchReportsForCached();
      setIsLoadingUsers(false);
      return;
    }

    fetchUsers();

    return () => {
      mounted = false;
      controller.abort();
    };
  }, [user, token]);

  useEffect(() => {
    if (!user || !token || (user.role !== "Admin" && user.role !== "Super Admin")) return;

    let mounted = true;
    const controller = new AbortController();

    const cachedPlans = JSON.parse(localStorage.getItem("plans"));
    const cacheTime = JSON.parse(localStorage.getItem("plansCacheTime"));
    const isCacheValid = cacheTime && Date.now() - cacheTime < 5 * 60 * 1000;

    if (cachedPlans && isCacheValid) {
      setAvailablePlans(cachedPlans);
      setIsLoadingPlans(false);
      return;
    }

    const fetchPlans = async () => {
      setIsLoadingPlans(true);
      try {
        const res = await axios.get("https://backend.emerald.azadnoori.com/api/v1/plans", {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true,
        });
        if (mounted) {
          setAvailablePlans(res.data.data.Plan || []);
          localStorage.setItem("plans", JSON.stringify(res.data.data.Plan || []));
          localStorage.setItem("plansCacheTime", JSON.stringify(Date.now()));
        }
      } catch (error) {
        if (error.name === "AbortError") return;
        if (mounted) {
          if (error.response?.status === 401) {
            toast.error("Session expired. Please log in again.", {
              position: "top-right",
              icon: <X size={20} className="text-red-600" />,
            });
            navigate("/login");
          } else if (error.response?.status === 403) {
            toast.error("Not authorized to fetch plans.", {
              position: "top-right",
              icon: <X size={20} className="text-red-600" />,
            });
          } else {
            toast.error(error.response?.data?.message || "Failed to fetch plans.", {
              position: "top-right",
              icon: <X size={20} className="text-red-600" />,
            });
          }
        }
      } finally {
        if (mounted) setIsLoadingPlans(false);
      }
    };

    fetchPlans();

    return () => {
      mounted = false;
      controller.abort();
    };
  }, [user, token]);

  const filteredUsers = users.filter(
    (user) =>
      user.firstname?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.lastname?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.role?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleViewUserDetails = (userId) => {
    navigate(`/users/${userId}`);
  };

  const handleDeleteUser = (e, userId) => {
    e.stopPropagation();
    const targetUser = users.find((u) => u.id === userId);

    if (targetUser.role === "Super Admin" && user.role !== "Super Admin") {
      toast.error("Admins cannot delete Super Admin accounts.", {
        position: "top-right",
        icon: <X size={20} className="text-red-600" />,
      });
      return;
    }

    setUserToDelete(userId);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteUser = async () => {
    const targetUser = users.find((u) => u.id === userToDelete);

    if (targetUser.role === "Super Admin" && user.role !== "Super Admin") {
      toast.error("Admins cannot delete Super Admin accounts.", {
        position: "top-right",
        icon: <X size={20} className="text-red-600" />,
      });
      setShowDeleteConfirm(false);
      setUserToDelete(null);
      return;
    }

    try {
      await axios.delete(`https://backend.emerald.azadnoori.com/api/v1/users/${userToDelete}`, {
        headers: { Authorization: `Bearer ${token}` },
        withCredentials: true,
      });
      localStorage.removeItem("users");
      await fetchUsers();
      setShowDeleteConfirm(false);
      setUserToDelete(null);
      toast.success("User successfully deleted!", {
        position: "top-right",
        autoClose: 3000,
        icon: <CheckCircle size={20} className="text-emerald-600" />,
      });

      if (userToDelete === user.id && user.role === "Super Admin") {
        toast.info("Your account has been deleted. Logging out...", {
          position: "top-right",
          icon: <X size={20} className="text-blue-600" />,
        });
        navigate("/login");
      }
    } catch (error) {
      const errorMessage =
        error.response?.data?.message || "Failed to delete user. Please try again.";
      if (error.response?.status === 401) {
        toast.error("Session expired. Please log in again.", {
          position: "top-right",
          icon: <X size={20} className="text-red-600" />,
        });
        navigate("/login");
      } else if (error.response?.status === 403) {
        toast.error("Not authorized to delete user.", {
          position: "top-right",
          icon: <X size={20} className="text-red-600" />,
        });
      } else {
        toast.error(errorMessage, {
          position: "top-right",
          icon: <X size={20} className="text-red-600" />,
        });
      }
    }
  };

  const handleEditUser = (e, user) => {
    e.stopPropagation();

    if (user.role === "Super Admin" && user.id !== user.id) {
      toast.error("Admins cannot edit Super Admin accounts.", {
        position: "top-right",
        icon: <X size={20} className="text-red-600" />,
      });
      return;
    }

    setEditingUser({ ...user, password: "", confirmPassword: "" });
    setShowEditModal(true);
  };

  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setEditingUser({ ...editingUser, [name]: value });
  };

  const saveUserChanges = async () => {
    if (editingUser.role === "Super Admin" && user.role !== "Super Admin") {
      toast.error("Admins cannot edit Super Admin accounts.", {
        position: "top-right",
        icon: <X size={20} className="text-red-600" />,
      });
      setShowEditModal(false);
      setEditingUser(null);
      return;
    }

    if (editingUser.password || editingUser.confirmPassword) {
      if (editingUser.password.length < 6) {
        toast.error("Password must be at least 6 characters long.", {
          position: "top-right",
          icon: <X size={20} className="text-red-600" />,
        });
        return;
      }
      if (editingUser.password !== editingUser.confirmPassword) {
        toast.error("Passwords do not match.", {
          position: "top-right",
          icon: <X size={20} className="text-red-600" />,
        });
        return;
      }
    }

    try {
      const updateData = {
        firstname: editingUser.firstname,
        lastname: capitalizeFirstLetter(editingUser.lastname),
        plan: editingUser.plan,
      };
      if (editingUser.password) {
        updateData.password = editingUser.password;
        updateData.confirmPassword = editingUser.confirmPassword;
      }

      await axios.patch(
        `https://backend.emerald.azadnoori.com/api/v1/users/update/${editingUser.id}`,
        updateData,
        {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true,
        }
      );
      localStorage.removeItem("users");
      await fetchUsers();
      setShowEditModal(false);
      setEditingUser(null);
      toast.success("User successfully updated!", {
        position: "top-right",
        autoClose: 3000,
        icon: <CheckCircle size={20} className="text-emerald-600" />,
      });
    } catch (error) {
      if (error.response?.status === 401) {
        toast.error("Session expired. Please log in again.", {
          position: "top-right",
          icon: <X size={20} className="text-red-600" />,
        });
        navigate("/login");
      } else if (error.response?.status === 403) {
        toast.error("Not authorized to update user.", {
          position: "top-right",
          icon: <X size={20} className="text-red-600" />,
        });
      } else {
        toast.error(error.response?.data?.message || "Failed to update user.", {
          position: "top-right",
          icon: <X size={20} className="text-red-600" />,
        });
      }
    }
  };

  const handleRefresh = () => {
    localStorage.removeItem("users");
    localStorage.removeItem("plans");
    fetchUsers();
  };

  useEffect(() => {
    const handleStorageChange = (event) => {
      if (event.key === "userAdded") {
        localStorage.removeItem("users");
        fetchUsers();
      }
    };
    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  const container = {
    hidden: { opacity: 0 },
    show: { opacity: 1, transition: { staggerChildren: 0.1 } },
  };

  const item = { hidden: { opacity: 0, y: 20 }, show: { opacity: 1, y: 0 } };

  return (
    <div>
      <div className="flex items-center justify-between mb-6 flex-col sm:flex-row gap-4">
        <div className="relative w-full sm:max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
          <input
            type="text"
            placeholder="Search users..."
            className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-600"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="flex items-center gap-4 text-sm text-gray-500">
          <div className="flex items-center">
            <UserCog className="mr-2" size={16} />
            <span>Total Users: {users.length}</span>
          </div>
          <button
            onClick={handleRefresh}
            className="p-2 rounded-full hover:bg-gray-100 text-gray-500 hover:text-emerald-600 active:bg-gray-200 transition-colors"
            title="Refresh data"
          >
            <RefreshCw size={16} />
          </button>
        </div>
      </div>

      {isLoadingUsers && (
        <div className="text-center py-8 text-gray-500">Loading users...</div>
      )}

      {!isLoadingUsers && (
        <motion.div
          className="grid gap-4 md:grid-cols-2 lg:grid-cols-3"
          variants={container}
          initial="hidden"
          animate="show"
        >
          {filteredUsers.length > 0 ? (
            filteredUsers.map((u) => (
              <motion.div
                key={u.id}
                variants={item}
                className="bg-white border rounded-lg p-4 flex flex-col sm:flex-row items-start sm:items-center justify-between cursor-pointer transition-all hover:bg-gray-50 w-full"
                onClick={() => handleViewUserDetails(u.id)}
              >
                <div className="flex items-center gap-3 min-w-0 flex-1 mb-3 sm:mb-0">
                  <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center overflow-hidden flex-shrink-0">
                    {u.profile_image ? (
                      <img
                        src={`https://backend.emerald.azadnoori.com${u.profile_image}`}
                        crossOrigin="anonymous"
                        alt={`${u.firstname} ${u.lastname}`}
                        className="w-full h-full object-cover"
                        loading="lazy"
                        onError={(e) => {
                          e.target.style.display = "none";
                          e.target.nextSibling.style.display = "flex";
                        }}
                      />
                    ) : null}
                    <span
                      className="text-gray-600 font-medium"
                      style={{ display: u.profile_image ? "none" : "flex" }}
                    >
                      {capitalizeFirstLetter(u.firstname)?.charAt(0) || "U"}
                    </span>
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-medium text-gray-900 truncate">
                      {u.firstname + " " + capitalizeFirstLetter(u.lastname || "")}
                    </h3>
                    <p className="text-sm text-gray-500 truncate">{u.email}</p>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      <span className="text-xs bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded-full">
                        {u.plan}
                      </span>
                      <span className="text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full">
                        Reports: {userReports[u.id]?.count || 0}
                      </span>
                      <span className="text-xs bg-purple-100 text-purple-800 px-2 py-0.5 rounded-full">
                        Images: {userReports[u.id]?.imageCount || 0}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0 w-full sm:w-auto justify-between sm:justify-end">
                  <div className="text-left sm:text-right flex-shrink-0">
                    <span className="text-sm font-medium text-gray-600">{u.role}</span>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                      onClick={(e) => handleEditUser(e, u)}
                      className="p-2 rounded-full hover:bg-gray-100 text-gray-500 hover:text-emerald-600 active:bg-gray-200 transition-colors"
                      disabled={u.role === "Super Admin" && user.role !== "Super Admin"}
                    >
                      <Edit size={16} />
                    </button>
                    {u.role === "Super Admin" ? (
                      <span className="flex items-center text-xs text-yellow-600 bg-yellow-100 px-2 py-0.5 rounded-full">
                        <AlertCircle size={14} className="mr-1" />
                        Super Admin
                      </span>
                    ) : (
                      <button
                        onClick={(e) => handleDeleteUser(e, u.id)}
                        className="p-2 rounded-full hover:bg-gray-100 text-gray-500 hover:text-red-600 active:bg-gray-200 transition-colors"
                      >
                        <Trash size={16} />
                      </button>
                    )}
                    <ChevronRight size={20} className="text-gray-400" />
                  </div>
                </div>
              </motion.div>
            ))
          ) : (
            <div className="text-center py-8 text-gray-500">No users found</div>
          )}
        </motion.div>
      )}

      <AnimatePresence>
        {showDeleteConfirm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-xl p-4 sm:p-6 w-full max-w-sm sm:max-w-md"
            >
              <h3 className="text-lg sm:text-xl font-bold text-gray-800 mb-2">
                Confirm Deletion
              </h3>
              <p className="text-gray-600 mb-4 sm:mb-6 text-sm sm:text-base">
                Are you sure you want to delete this user? This action cannot be undone.
              </p>
              <div className="flex flex-col sm:flex-row justify-end space-y-3 sm:space-y-0 sm:space-x-4">
                <button
                  onClick={() => setShowDeleteConfirm(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 active:bg-gray-100 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmDeleteUser}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 active:bg-red-800 transition-colors flex items-center"
                >
                  <Trash size={18} className="mr-2" />
                  Delete
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showEditModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-xl p-4 sm:p-6 w-full max-w-sm sm:max-w-md"
            >
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg sm:text-xl font-bold text-gray-800">Edit User</h3>
                <button
                  onClick={() => setShowEditModal(false)}
                  className="p-1 rounded-full hover:bg-gray-100 active:bg-gray-200"
                >
                  <X size={20} />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label
                    htmlFor="firstname"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    First Name
                  </label>
                  <input
                    id="firstname"
                    name="firstname"
                    type="text"
                    value={editingUser?.firstname || ""}
                    onChange={handleEditInputChange}
                    className="w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-600"
                    required
                  />
                </div>
                <div>
                  <label
                    htmlFor="lastname"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Last Name
                  </label>
                  <input
                    id="lastname"
                    name="lastname"
                    type="text"
                    value={editingUser?.lastname || ""}
                    onChange={handleEditInputChange}
                    className="w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-600"
                    required
                  />
                </div>
                <div>
                  <label
                    htmlFor="password"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    New Password (leave blank to keep unchanged)
                  </label>
                  <input
                    id="password"
                    name="password"
                    type="password"
                    value={editingUser?.password || ""}
                    onChange={handleEditInputChange}
                    className="w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-600"
                    placeholder="Enter new password"
                  />
                </div>
                <div>
                  <label
                    htmlFor="confirmPassword"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Confirm New Password
                  </label>
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type="password"
                    value={editingUser?.confirmPassword || ""}
                    onChange={handleEditInputChange}
                    className="w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-600"
                    placeholder="Confirm new password"
                  />
                </div>
                <div>
                  <label
                    htmlFor="plan"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Plan
                  </label>
                  <select
                    id="plan"
                    name="plan"
                    value={editingUser?.plan || ""}
                    onChange={handleEditInputChange}
                    className="w-full p-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-600"
                    disabled={isLoadingPlans}
                    required
                  >
                    <option value="">Select a plan</option>
                    {availablePlans.map((plan) => (
                      <option key={plan.id} value={plan.name}>
                        {plan.name}
                      </option>
                    ))}
                  </select>
                  {isLoadingPlans && (
                    <p className="mt-1 text-sm text-gray-600">Loading plans...</p>
                  )}
                </div>
              </div>

              <div className="flex justify-end mt-6">
                <button
                  onClick={saveUserChanges}
                  className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 active:bg-emerald-800 transition-colors flex items-center"
                >
                  <Check size={18} className="mr-2" />
                  Save Changes
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}